<script setup lang="ts">
import { computed, ref, watchEffect, type PropType } from 'vue';
import { useVModel } from '@vueuse/core';
import { Color } from '@precon-tools-suite-by-shared/precon-graphql-types';
import {
    type ColorTint,
    useColorResolver,
} from '@precon-tools-suite-by-shared/shared-colors-ui';
import SelectButton from 'primevue/selectbutton';

const props = defineProps({
    question: {
        type: String,
        required: true,
    },
    answer: {
        type: [String, null] as PropType<string | null>,
        default: null,
        required: true,
    },
    options: {
        type: Array as PropType<string[]>,
        default: () => [],
        required: true,
    },
    helperComment: {
        type: String as PropType<string | null>,
        default: null,
    },
});

const emit = defineEmits<{
    (e: 'update:answer', answer: string | null): void;
    (e: 'collapsed'): void;
    (e: 'expanded'): void;
}>();

const colorResolver = useColorResolver();

const chevronStyle = computed(() => ({
    opacity: props.helperComment ? 1 : 0,
}));

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const answer = useVModel(props, 'answer', emit);
const question = computed(() => props.question);
const options = computed(() => props.options);

const color = (tint?: ColorTint) => colorResolver(Color.Blue, tint);
const collapsed = ref<boolean>(true);

const selectedAnswer = ref<string | null>(null);
watchEffect(() => {
    selectedAnswer.value = answer.value;
});

// ------------------------------------------------------------------------------------------------------------------ //
//  ACTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

function collapse() {
    collapsed.value = true;
    emit('collapsed');
}

function expand() {
    if (!props.helperComment) return;
    emit('expanded');
    collapsed.value = false;
}

function toggle() {
    if (collapsed.value) {
        expand();
    } else {
        collapse();
    }
}
</script>
<template>
    <div class="answer-predicate-question-input">
        <div
            class="question-card p-card p-component"
            :class="{ 'question-card-collapsed': collapsed }"
        >
            <div
                class="question-card-ribbon"
                :style="{
                    'background-color': color('500').rgbString(),
                }"
            />
            <div class="question-card-header">
                <div class="question-card-title" @click="toggle">
                    <div class="question-card-chevron" :style="chevronStyle">
                        <i
                            class="pi pi-chevron-right px-1 text-500 question-card-toggle"
                        />
                    </div>
                    <div class="question-card-question">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <h5 v-html="question" />
                    </div>
                </div>
                <div class="question-card-answer">
                    <SelectButton
                        v-model="answer"
                        :options="options"
                        class="predicate-value-selectbutton"
                    />
                </div>
            </div>
            <div class="question-card-content">
                <div class="question-card-body">
                    <span v-text="helperComment" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.p-card.question-card {
    position: relative;

    .question-card-toggle {
        transition: 0.2s;
        transform: rotate(90deg);
    }

    &.question-card-collapsed {
        .question-card-toggle {
            transition: 0.2s;
            transform: rotate(0deg);
        }

        .question-card-content {
            display: none;
        }
    }

    .question-card-ribbon {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 6px;
    }

    .question-card-header {
        display: flex;
        width: 100%;

        .question-card-title {
            padding: 0.5rem;
            flex-grow: 1;
            display: flex;
            align-items: center;
            cursor: pointer;

            &:hover {
                background-color: var(--surface-50);
            }

            .question-card-chevron {
                flex-shrink: 0;
                width: fit-content;
            }

            .question-card-question {
                flex-grow: 1;
                h5 {
                    font-size: 1rem;
                    user-select: none;
                    margin: 0;
                    padding: 0;
                }
            }
        }

        .question-card-answer {
            flex-shrink: 1;

            .p-selectbutton {
                display: flex;
                flex-wrap: nowrap;
                .p-button {
                    white-space: nowrap;
                }
                .p-button-label {
                    margin-right: 10px;
                }
            }
        }
    }

    .question-card-content {
        display: block;
        padding-left: 8px;

        .question-card-body {
            padding: 0.5rem;
        }

        .question-card-footer {
            padding: 0.5rem;
            display: flex;
            justify-content: end;
            align-items: center;
            gap: 0.5rem;
        }
    }
}
</style>
